import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type Language = 'en' | 'de' | 'fr' | 'it' | 'nl' | 'hr' | 'pl';

export type ItineraryTier = 'budget' | 'midrange' | 'luxury';

interface AuthUser {
  id: number;
  email: string;
  role: string;
}

export interface GuestItineraryItem {
  slug: string;
  locationId: number;
  locationName: string;
  locationImage: string;
  category: string;
  dayNumber: number;
  orderIndex: number;
  notes: string;
}

function todayIso(): string {
  return new Date().toISOString().split('T')[0];
}

interface AppState {
  language: Language;
  setLanguage: (lang: Language) => void;

  theme: 'light' | 'dark';
  toggleTheme: () => void;
  
  compareList: string[];
  addToCompare: (slug: string) => void;
  removeFromCompare: (slug: string) => void;
  clearCompare: () => void;

  guestItinerary: GuestItineraryItem[];
  addToGuestItinerary: (item: Omit<GuestItineraryItem, 'orderIndex'>) => void;
  removeFromGuestItinerary: (slug: string) => void;
  updateGuestItineraryItem: (slug: string, changes: Partial<Pick<GuestItineraryItem, 'dayNumber' | 'notes'>>) => void;
  clearGuestItinerary: () => void;

  itineraryStartDate: string;
  setItineraryStartDate: (date: string) => void;
  itineraryTravelers: number;
  setItineraryTravelers: (n: number) => void;
  itineraryTier: ItineraryTier;
  setItineraryTier: (tier: ItineraryTier) => void;

  user: AuthUser | null;
  setUser: (user: AuthUser | null) => void;
  logout: () => void;

  // Per-session CSRF token, fetched from `/api/auth/me`, `/api/auth/login`,
  // and `/api/auth/register`, then echoed back in the `X-CSRF-Token` header
  // on every state-changing request. Intentionally NOT persisted to
  // localStorage (see `partialize` below) — a stale token from a prior
  // session would always mismatch and silently break every admin write.
  csrfToken: string | null;
  setCsrfToken: (token: string | null) => void;

  authModalOpen: boolean;
  setAuthModalOpen: (open: boolean) => void;

  // Per-page localized URL map. Pages that have non-generic per-language URLs
  // (e.g. blog posts with localized slugs) set this so the Navbar language
  // switcher uses the correct URL instead of the generic switchLangPath transform.
  // Must be cleared (set to null) when the page unmounts.
  pageAlternates: Record<string, string> | null;
  setPageAlternates: (urls: Record<string, string> | null) => void;
}

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      language: 'en',
      setLanguage: (language) => set({ language }),

      theme: 'light',
      toggleTheme: () => set((state) => ({ theme: state.theme === 'light' ? 'dark' : 'light' })),
      
      compareList: [],
      addToCompare: (slug) => 
        set((state) => ({
          compareList: state.compareList.includes(slug) || state.compareList.length >= 3
            ? state.compareList 
            : [...state.compareList, slug]
        })),
      removeFromCompare: (slug) =>
        set((state) => ({
          compareList: state.compareList.filter((s) => s !== slug)
        })),
      clearCompare: () => set({ compareList: [] }),

      guestItinerary: [],
      addToGuestItinerary: (item) =>
        set((state) => {
          if (state.guestItinerary.some((i) => i.slug === item.slug)) return state;
          const orderIndex = state.guestItinerary.filter((i) => i.dayNumber === item.dayNumber).length;
          return { guestItinerary: [...state.guestItinerary, { ...item, orderIndex }] };
        }),
      removeFromGuestItinerary: (slug) =>
        set((state) => ({
          guestItinerary: state.guestItinerary
            .filter((i) => i.slug !== slug)
            .map((i, idx) => ({ ...i, orderIndex: idx })),
        })),
      updateGuestItineraryItem: (slug, changes) =>
        set((state) => ({
          guestItinerary: state.guestItinerary.map((i) =>
            i.slug === slug ? { ...i, ...changes } : i
          ),
        })),
      clearGuestItinerary: () => set({ guestItinerary: [] }),

      itineraryStartDate: todayIso(),
      setItineraryStartDate: (itineraryStartDate) => set({ itineraryStartDate }),
      itineraryTravelers: 2,
      setItineraryTravelers: (itineraryTravelers) => set({ itineraryTravelers }),
      itineraryTier: 'midrange',
      setItineraryTier: (itineraryTier) => set({ itineraryTier }),

      user: null,
      setUser: (user) => set({ user }),
      logout: () => set({ user: null, csrfToken: null }),

      csrfToken: null,
      setCsrfToken: (csrfToken) => set({ csrfToken }),

      authModalOpen: false,
      setAuthModalOpen: (authModalOpen) => set({ authModalOpen }),

      pageAlternates: null,
      setPageAlternates: (pageAlternates) => set({ pageAlternates }),
    }),
    {
      name: 'smart-croatia-storage',
      partialize: (state) => ({
        language: state.language,
        theme: state.theme,
        compareList: state.compareList,
        guestItinerary: state.guestItinerary,
        itineraryStartDate: state.itineraryStartDate,
        itineraryTravelers: state.itineraryTravelers,
        itineraryTier: state.itineraryTier,
        user: state.user,
      }),
    }
  )
);
